import React, { useEffect, useState } from "react";
import { Form, Formik, FormikProps } from "formik";
import { useMutation } from "@apollo/client";
import { Title } from "../../../../../../stories/Title/Title";
import { Button } from "../../../../../../stories/Button/Button";
import Close from "../../../../../../images/Close.svg";
import InputHoc from "../../../../../common/Input/Index";
import { LabelProps } from "../../../../../../utils/types";
import { IObjectKeys } from "../../../../../../utils/formTypes";
import { itemDataType } from "./ResolveDetails";
import { UpdateItemDetails } from "../../../masters/subsidary/junctions/itemdetails/queries/mutations";
import {
  UpdateItemDetailsResult,
  UpdateItemDetailsVar,
} from "../../../masters/subsidary/junctions/itemdetails/queries/types";
import useUrlParams from "../../../../../../customHooks/useUrlParams";
import useLoggedInUserDetails from "../../../../../../customHooks/useLoggedInUserDetails";
import {
  DEFAULT_TIME,
  EMPTY_STRING,
  emptyMessage,
} from "../../../../../../utils/constants";
import { Operation } from "../../../../../../utils/Enum.types";
import MessageModal from "../../../../../../customComponents/MessageModal";
import {
  toInputStandardDate,
  toIsoDate,
} from "../../../../../../utils/UtilFunctions";

interface Props {
  itemData: itemDataType;
  setModalFlag: React.Dispatch<React.SetStateAction<boolean>>;
  onUpdated: (updated: Partial<itemDataType>) => void;
}

const editFields: LabelProps[] = [
  {
    labelName: "Serial Number",
    inputName: "prodSerialNo",
    dataType: "text",
    autoFocus: true,
  },
  {
    labelName: "Batch",
    inputName: "prodBatchNo",
    dataType: "text",
  },
  {
    labelName: "Warranty Start Date",
    inputName: "prodWarrantyStartDate",
    dataType: "date",
  },
  {
    labelName: "Warranty End Date",
    inputName: "prodWarrantyEndDate",
    dataType: "date",
  },
  {
    labelName: "Installation Date",
    inputName: "prodInstallationEndDate",
    dataType: "date",
  },
  {
    labelName: "Geo Location",
    inputName: "siteLocation",
    dataType: "text",
  },
  {
    labelName: "Remarks",
    inputName: "remark",
    dataType: "text",
  },
];

interface editFormDataType extends IObjectKeys {
  prodSerialNo: string;
  prodBatchNo: string;
  prodWarrantyStartDate: string;
  prodWarrantyEndDate: string;
  prodInstallationEndDate: string;
  siteLocation: string;
  remark: string;
}

const emptyFormData: editFormDataType = {
  prodSerialNo: "",
  prodBatchNo: "",
  prodWarrantyStartDate: "",
  prodWarrantyEndDate: "",
  prodInstallationEndDate: "",
  siteLocation: "",
  remark: "",
};

const toDateInput = (value: string) => {
  if (!value || value === "-") return EMPTY_STRING;

  let formatted = value;

  if (/^\d{2}-\d{2}-\d{4}$/.test(value)) {
    const [day, month, year] = value.split("-");
    formatted = `${year}-${month}-${day}`;
  } else if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    formatted = toInputStandardDate(value);
  }

  return formatted && formatted !== DEFAULT_TIME ? formatted : EMPTY_STRING;
};

const EditItemDetails = ({ itemData, setModalFlag, onUpdated }: Props) => {
  const { customer_id } = useUrlParams();
  const { user_details } = useLoggedInUserDetails();
  const [message, setMessage] = useState(emptyMessage);
  const [formData, setFormData] = useState<editFormDataType>(emptyFormData);

  const [updateItemDetails] = useMutation<
    UpdateItemDetailsResult,
    UpdateItemDetailsVar
  >(UpdateItemDetails, {
    onError: (e) => {
      setMessage({
        flag: true,
        message: e.message,
        operation: Operation.NONE,
      });
    },
  });

  useEffect(() => {
    setFormData({
      prodSerialNo: itemData.prodSerialNo ?? "",
      prodBatchNo: itemData.prodBatchNo ?? "",
      prodWarrantyStartDate: toDateInput(itemData.prodWarrantyStartDate),
      prodWarrantyEndDate: toDateInput(itemData.prodWarrantyEndDate),
      prodInstallationEndDate: toDateInput(itemData.prodInstallationEndDate),
      siteLocation: itemData.siteLocation ?? "",
      remark: itemData.remark ?? "",
    });
  }, [itemData]);

  const handleValueChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    meta: FormikProps<editFormDataType>
  ) => {
    const { name, value } = e.target as HTMLInputElement;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    meta.handleChange(e);
  };

  const handleSubmit = () => {
    if (!customer_id || !itemData.id) return;

    const [latitude, longitude] = formData.siteLocation
      .split(",")
      .map((val) => val.trim());

    updateItemDetails({
      variables: {
        customerID: customer_id,
        itemDetailsID: Number(itemData.id),
        user_details,
        input: {
          prodSerialNo: formData.prodSerialNo,
          prodBatchNo: formData.prodBatchNo,
          prodWarrantyStartDate: formData.prodWarrantyStartDate
            ? toIsoDate(formData.prodWarrantyStartDate)
            : undefined,
          prodWarrantyEndDate: formData.prodWarrantyEndDate
            ? toIsoDate(formData.prodWarrantyEndDate)
            : undefined,
          prodInstallationEndDate: formData.prodInstallationEndDate
            ? toIsoDate(formData.prodInstallationEndDate)
            : undefined,
          prodLatitude: latitude ?? EMPTY_STRING,
          prodLongitude: longitude ?? EMPTY_STRING,
           remark: formData.remark,
        } as UpdateItemDetailsVar["input"],
      },
    }).then(({ data }) => {
      if (data && data.UpdateItemDetails) {
        onUpdated({
          prodSerialNo: formData.prodSerialNo,
          prodBatchNo: formData.prodBatchNo,
          prodWarrantyStartDate: formData.prodWarrantyStartDate,
          prodWarrantyEndDate: formData.prodWarrantyEndDate,
          prodInstallationEndDate: formData.prodInstallationEndDate,
          siteLocation: formData.siteLocation,
          remark: formData.remark,
        });
        setMessage({
          flag: true,
          message: "Product details updated successfully",
          operation: Operation.UPDATE,
        });
      }
    });
  };

  const handleClose = () => {
    if (message.operation !== Operation.NONE) {
      setModalFlag(false);
    }
    setMessage(emptyMessage);
  };

  return (
    <>
      <div className="replace-stock">
        <div className="replace-stock__title">
          <Title>Edit Product Information</Title>
          <img src={Close} alt="" onClick={() => setModalFlag(false)} />
        </div>

        <Formik
          initialValues={formData}
          onSubmit={handleSubmit}
          enableReinitialize
          validateOnChange
        >
          {(meta) => {
            return (
              <Form className="replace-stock__form">
                
                    {editFields.map((field: LabelProps, key: React.Key) => {
                      return (
                        <React.Fragment key={key}>
                          <InputHoc
                            LabelName={field.labelName}
                            values={formData[field.inputName]}
                            name={field.inputName}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>
                            ) => {
                              handleValueChange(e, meta);
                            }}
                            required={field.required}
                            autoFocus={field.autoFocus}
                            type={field.dataType}
                            {...(field.dataType === "date"
                              ? { min: "1000-01-01", max: "9999-12-31" }
                              : {})}
                            onBlur={meta.handleBlur}
                          />
                        </React.Fragment>
                      );
                    })}
            

                <Button type="submit" mode="save" className="submit" />
                <Button
                  mode="cancel"
                  type="button"
                  onClick={() => setModalFlag(false)}
                />
              </Form>
            );
          }}
        </Formik>
      </div>

      <MessageModal
        modalFlag={message.flag!}
        value={message.message!}
        handleClose={handleClose}
        operation={message.operation!}
      />
    </>
  );
};

export default EditItemDetails;
